import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Receipt } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * NewInvoiceDialog
 * ----------------
 * Fast "New invoice" form triggered from the Customer profile. Customer
 * name/contact/address/property come from the outer profile (customer_id
 * is already known), so the form only asks for:
 *   - Title
 *   - Line items (name, qty, unit_price, taxable, optional service catalog pick)
 *   - Notes
 *   - Discount, tax
 *
 * On submit it POSTs /api/invoices which:
 *   1) assigns the next INV-#### number,
 *   2) applies the business's default Invoice template on send/view (via
 *      _snapshot_design_on_doc kind='invoice'), and
 *   3) returns the new invoice — we redirect to its profile.
 *
 * The button also honors an existing conversion path — if the customer
 * already has estimates that haven't been invoiced, the outer profile shows
 * that shortcut. This dialog is the "blank invoice / mixed line items"
 * escape hatch.
 */
export default function NewInvoiceDialog({ open, onClose, customerId, onCreated }) {
  const [title, setTitle] = useState("");
  const [rows, setRows] = useState([{ name: "", quantity: 1, unit_price: 0, taxable: true, service_id: null }]);
  const [notes, setNotes] = useState("");
  const [taxRate, setTaxRate] = useState("");
  const [discountType, setDiscountType] = useState("percent");
  const [discountValue, setDiscountValue] = useState(0);
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Reset each time we open
    setTitle("");
    setRows([{ name: "", quantity: 1, unit_price: 0, taxable: true, service_id: null }]);
    setNotes("");
    setTaxRate("");
    setDiscountType("percent");
    setDiscountValue(0);
    // Load the service catalog for the picker (best effort — errors are fine)
    api.get("/services").then(({ data }) => setServices(data || [])).catch(() => setServices([]));
  }, [open]);

  const setRow = (i, patch) => setRows(prev => prev.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const removeRow = (i) => setRows(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);
  const addRow = () => setRows(prev => [...prev, { name: "", quantity: 1, unit_price: 0, taxable: true, service_id: null }]);

  const applyService = (i, serviceId) => {
    const svc = services.find(s => s.id === serviceId);
    if (!svc) { setRow(i, { service_id: null }); return; }
    setRow(i, {
      service_id: svc.id,
      name: svc.name || "",
      unit_price: svc.default_price ?? svc.base_price ?? 0,
      taxable: svc.taxable !== false,
    });
  };

  const submit = async () => {
    const cleanRows = rows
      .map(r => ({ ...r, quantity: Number(r.quantity) || 0, unit_price: Number(r.unit_price) || 0 }))
      .filter(r => r.name && r.quantity > 0);
    if (cleanRows.length === 0) {
      toast.error("Add at least one line item with a name and quantity.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customer_id: customerId,
        title: title || "",
        line_items: cleanRows.map(r => ({
          name: r.name,
          description: "",
          quantity: r.quantity,
          unit_price: r.unit_price,
          taxable: !!r.taxable,
          addons: [],
          service_id: r.service_id || null,
        })),
        discount_type: discountType,
        discount_value: Number(discountValue) || 0,
        tax_rate: taxRate === "" ? null : Number(taxRate),
        notes: notes || "",
      };
      const { data: inv } = await api.post("/invoices", payload);
      toast.success(`Invoice ${inv.invoice_number} created`);
      onCreated?.(inv);
      onClose?.();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "";
      toast.error(`Create invoice failed${status ? ` (${status})` : ""}: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v)=>!v && onClose?.()}>
      <DialogContent className="max-w-3xl" data-testid="new-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-emerald-600"/>
            New invoice
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-mono-accent uppercase tracking-widest text-zinc-500 mb-1 block">Title</label>
            <Input value={title} onChange={(e)=>setTitle(e.target.value)} placeholder="e.g. Q1 service call" data-testid="new-invoice-title"/>
          </div>

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-mono-accent uppercase tracking-widest text-zinc-500">Line items</label>
              <Button size="sm" variant="outline" onClick={addRow} data-testid="new-invoice-add-line-btn">
                <Plus className="w-3.5 h-3.5 mr-1"/> Add line
              </Button>
            </div>
            <div className="border border-zinc-200 rounded-md divide-y divide-zinc-100">
              {rows.map((r, i) => (
                <div key={i} className="p-2 grid grid-cols-12 gap-2 items-center" data-testid={`new-invoice-line-${i}`}>
                  <div className="col-span-4">
                    <Input placeholder="Service or item" value={r.name} onChange={(e)=>setRow(i, { name: e.target.value })} data-testid={`new-invoice-line-name-${i}`}/>
                  </div>
                  <div className="col-span-3">
                    {services.length > 0 && (
                      <Select value={r.service_id || ""} onValueChange={(v)=>applyService(i, v)}>
                        <SelectTrigger data-testid={`new-invoice-line-service-${i}`}>
                          <SelectValue placeholder="From catalog…"/>
                        </SelectTrigger>
                        <SelectContent>
                          {services.map(s => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  <div className="col-span-2">
                    <Input type="number" min={0} step="0.01" value={r.quantity} onChange={(e)=>setRow(i, { quantity: e.target.value })} data-testid={`new-invoice-line-qty-${i}`}/>
                  </div>
                  <div className="col-span-2">
                    <Input type="number" min={0} step="0.01" value={r.unit_price} onChange={(e)=>setRow(i, { unit_price: e.target.value })} data-testid={`new-invoice-line-price-${i}`}/>
                  </div>
                  <div className="col-span-1 text-right">
                    <button
                      className="text-rose-600 hover:bg-rose-50 rounded p-1"
                      onClick={()=>removeRow(i)}
                      disabled={rows.length === 1}
                      data-testid={`new-invoice-line-remove-${i}`}
                    >
                      <Trash2 className="w-4 h-4"/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Discount + tax */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs font-mono-accent uppercase tracking-widest text-zinc-500 mb-1 block">Discount type</label>
              <Select value={discountType} onValueChange={setDiscountType}>
                <SelectTrigger data-testid="new-invoice-discount-type"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percent (%)</SelectItem>
                  <SelectItem value="amount">Fixed ($)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-mono-accent uppercase tracking-widest text-zinc-500 mb-1 block">Discount value</label>
              <Input type="number" min={0} step="0.01" value={discountValue} onChange={(e)=>setDiscountValue(e.target.value)} data-testid="new-invoice-discount-value"/>
            </div>
            <div>
              <label className="text-xs font-mono-accent uppercase tracking-widest text-zinc-500 mb-1 block">Tax rate (%)</label>
              <Input type="number" min={0} step="0.01" placeholder="Business default" value={taxRate} onChange={(e)=>setTaxRate(e.target.value)} data-testid="new-invoice-tax-rate"/>
            </div>
          </div>

          <div>
            <label className="text-xs font-mono-accent uppercase tracking-widest text-zinc-500 mb-1 block">Notes</label>
            <Textarea rows={2} value={notes} onChange={(e)=>setNotes(e.target.value)} data-testid="new-invoice-notes"/>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="new-invoice-cancel-btn">Cancel</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={saving} onClick={submit} data-testid="new-invoice-submit-btn">
            {saving ? "Creating…" : "Create invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
